import { trackAiVoiceActivationSaved } from '@src/analytics/EventTracker';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useMemo, useState } from 'react';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

const logger = LogManager.getLogger('aiVoices:useVoiceActivation');

// Single source of truth for the showcase AI-dubbing activation writes (DE3),
// shared by the activation panel (ShowcaseAIVoicesPanel) and the audition panel's
// Add/Remove control (AIVoicesSection). Every mutation persists immediately
// (optimistic in-memory update → AlbumRepository.update → rollback + error
// toast on failure), so both surfaces stay in lockstep with no draft/save
// divergence.
// Callers must be `observer`s so they re-render on the underlying store change.
export function useVoiceActivation() {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const organization = videoCreator.organization;
  const [busy, setBusy] = useState(false);

  const dubbingEnabled = organization?.dubbingEnabled ?? false;
  const activatedVoices = useMemo(
    () => organization?.activatedAiVoices ?? [],
    [organization?.activatedAiVoices],
  );
  const activatedIds = useMemo(
    () => new Set(activatedVoices.map(v => v.id)),
    [activatedVoices],
  );

  // Apply `mutate` to the organization, persist, and roll back on failure.
  const persist = useCallback(
    async (mutate: () => void) => {
      if (!organization) return;
      const prevEnabled = organization.dubbingEnabled;
      const prevVoices = organization.activatedAiVoices;
      setBusy(true);
      try {
        mutate();
        await datoClientStore.albumRepository?.update(organization);
        trackAiVoiceActivationSaved({
          dubbing_enabled: organization.dubbingEnabled ?? false,
          activated_voice_count: (organization.activatedAiVoices ?? []).length,
        });
      } catch (err) {
        organization.dubbingEnabled = prevEnabled;
        organization.activatedAiVoices = prevVoices;
        const info = extractErrorInfo(err);
        logger.error('Failed to update AI Voices activation', info);
        videoCreator.showToast({
          state: 'error',
          message: `Could not update AI Voices. ${info?.errorMessage || 'Please try again.'}`,
        });
      } finally {
        setBusy(false);
      }
    },
    [organization, datoClientStore, videoCreator],
  );

  const setDubbingEnabled = useCallback(
    (next: boolean) =>
      persist(() => {
        if (organization) organization.dubbingEnabled = next;
      }),
    [persist, organization],
  );

  const addVoice = useCallback(
    (voice: AiVoiceRecord) =>
      persist(() => {
        if (organization) {
          organization.activatedAiVoices = [
            ...(organization.activatedAiVoices ?? []),
            voice,
          ];
        }
      }),
    [persist, organization],
  );

  const removeVoice = useCallback(
    (id: string) =>
      persist(() => {
        if (organization) {
          organization.activatedAiVoices = (
            organization.activatedAiVoices ?? []
          ).filter(v => v.id !== id);
        }
      }),
    [persist, organization],
  );

  const toggleVoice = useCallback(
    (voice: AiVoiceRecord) =>
      activatedIds.has(voice.id) ? removeVoice(voice.id) : addVoice(voice),
    [activatedIds, addVoice, removeVoice],
  );

  const isActivated = useCallback(
    (id: string) => activatedIds.has(id),
    [activatedIds],
  );

  return {
    organization,
    dubbingEnabled,
    activatedVoices,
    activatedIds,
    isActivated,
    busy,
    setDubbingEnabled,
    addVoice,
    removeVoice,
    toggleVoice,
  };
}
